import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export const OP_LABELS = {
  eq: "é igual a",
  neq: "é diferente de",
  gt: "é maior que",
  gte: "é maior ou igual a",
  lt: "é menor que",
  lte: "é menor ou igual a",
  contains: "contém",
};

const PRIORITIES = [
  { value: "low", label: "Baixa" },
  { value: "medium", label: "Média" },
  { value: "high", label: "Alta" },
  { value: "critical", label: "Crítica" },
];

const groupBy = (arr, key) =>
  arr.reduce((acc, it) => {
    (acc[it[key]] = acc[it[key]] || []).push(it);
    return acc;
  }, {});

function ValueInput({ field, value, onChange, testId }) {
  if (!field) return <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-white" data-testid={testId} />;
  if (field.type === "enum") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1 bg-white" data-testid={testId}><SelectValue placeholder="Escolha…" /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "boolean") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1 bg-white" data-testid={testId}><SelectValue placeholder="Escolha…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Sim</SelectItem>
          <SelectItem value="false">Não</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "number") {
    return <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-white" data-testid={testId} />;
  }
  return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Texto…" className="mt-1 bg-white" data-testid={testId} />;
}

export default function RuleForm({ form, setForm, catalog }) {
  const triggers = catalog?.triggers || [];
  const trigger = triggers.find((t) => t.value === form.trigger);
  const fields = trigger?.fields || [];
  const tokens = trigger?.tokens || [];
  const tokenLabels = catalog?.token_labels || {};
  const actions = catalog?.actions || [];
  const users = catalog?.users || [];
  const opsFor = (field) => (field ? catalog?.operators?.[field.type] || ["eq"] : Object.keys(OP_LABELS));

  const setTrigger = (v) => setForm({ ...form, trigger: v, conditions: [] });
  const addCondition = () => {
    const f = fields[0];
    const value = f?.type === "enum" ? f.options[0]?.value : f?.type === "boolean" ? "true" : "";
    setForm((s) => ({ ...s, conditions: [...s.conditions, { field: f?.key || "", op: "eq", value }] }));
  };
  const setCondition = (i, patch) => setForm((s) => ({ ...s, conditions: s.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const removeCondition = (i) => setForm((s) => ({ ...s, conditions: s.conditions.filter((_, idx) => idx !== i) }));
  const addAction = () => setForm((s) => ({ ...s, actions: [...s.actions, { type: "notify_managers_whatsapp", target: "", subject: "", message: "" }] }));
  const setAction = (i, patch) => setForm((s) => ({ ...s, actions: s.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));
  const removeAction = (i) => setForm((s) => ({ ...s, actions: s.actions.filter((_, idx) => idx !== i) }));
  const insertToken = (i, tok) => {
    const cur = form.actions[i].message || "";
    setAction(i, { message: `${cur}${cur && !cur.endsWith(" ") ? " " : ""}{${tok}}` });
  };

  return (
    <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2" data-testid="rule-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Nome *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white" data-testid="rule-name" />
        </div>
        <div>
          <Label>Quando (gatilho)</Label>
          <Select value={form.trigger} onValueChange={setTrigger}>
            <SelectTrigger className="mt-1 bg-white" data-testid="rule-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(groupBy(triggers, "group")).map(([g, items]) => (
                <SelectGroup key={g}>
                  <SelectLabel className="text-emerald-800">{g}</SelectLabel>
                  {items.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Descrição</Label>
        <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white" />
      </div>

      <div className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold tracking-tight">Condições (SE)</h4>
          <Button variant="outline" size="sm" onClick={addCondition} disabled={fields.length === 0} data-testid="rule-add-condition">
            <Plus className="h-3 w-3 mr-1" /> Condição
          </Button>
        </div>
        {form.conditions.length === 0 && (
          <div className="text-xs text-stone-500 font-body italic">Sem condições: a regra dispara em todo evento do gatilho.</div>
        )}
        {form.conditions.map((c, i) => {
          const field = fields.find((f) => f.key === c.field);
          return (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`rule-condition-${i}`}>
              <div className="col-span-4">
                <Label className="text-xs">Campo</Label>
                <Select value={c.field} onValueChange={(v) => {
                  const nf = fields.find((f) => f.key === v);
                  const nv = nf?.type === "enum" ? nf.options[0]?.value : nf?.type === "boolean" ? "true" : "";
                  setCondition(i, { field: v, op: "eq", value: nv });
                }}>
                  <SelectTrigger className="mt-1 bg-white" data-testid={`rule-condition-field-${i}`}><SelectValue placeholder="Campo…" /></SelectTrigger>
                  <SelectContent>{fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Operador</Label>
                <Select value={c.op} onValueChange={(v) => setCondition(i, { op: v })}>
                  <SelectTrigger className="mt-1 bg-white" data-testid={`rule-condition-op-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{opsFor(field).map((o) => <SelectItem key={o} value={o}>{OP_LABELS[o]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-4">
                <Label className="text-xs">Valor</Label>
                <ValueInput field={field} value={c.value} onChange={(v) => setCondition(i, { value: v })} testId={`rule-condition-value-${i}`} />
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="icon" onClick={() => removeCondition(i)} aria-label="Remover condição">
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold tracking-tight">Ações (ENTÃO)</h4>
          <Button variant="outline" size="sm" onClick={addAction} data-testid="rule-add-action"><Plus className="h-3 w-3 mr-1" /> Ação</Button>
        </div>
        {form.actions.map((a, i) => {
          const def = actions.find((x) => x.value === a.type);
          return (
            <Card key={i} className="p-3 mb-2 border-stone-200" data-testid={`rule-action-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className={def?.target === "none" ? "col-span-11" : "col-span-5"}>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={a.type} onValueChange={(v) => setAction(i, { type: v, target: "" })}>
                    <SelectTrigger className="mt-1 bg-white" data-testid={`rule-action-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{actions.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {def?.target === "user" && (
                  <div className="col-span-6">
                    <Label className="text-xs">Usuário</Label>
                    <Select value={a.target} onValueChange={(v) => setAction(i, { target: v })}>
                      <SelectTrigger className="mt-1 bg-white" data-testid={`rule-action-target-${i}`}><SelectValue placeholder="Escolha o usuário…" /></SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id} disabled={a.type === "notify_whatsapp" && !u.has_phone}>
                            {u.name} · {u.role}{a.type === "notify_whatsapp" && !u.has_phone ? " (sem telefone)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {def?.target === "priority" && (
                  <div className="col-span-6">
                    <Label className="text-xs">Prioridade da ocorrência</Label>
                    <Select value={a.target || "high"} onValueChange={(v) => setAction(i, { target: v })}>
                      <SelectTrigger className="mt-1 bg-white" data-testid={`rule-action-target-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-1">
                  <Button variant="ghost" size="icon" onClick={() => removeAction(i)} aria-label="Remover ação">
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                  </Button>
                </div>
                <div className="col-span-12">
                  <Label className="text-xs">{def?.target === "priority" ? "Título da ocorrência" : "Assunto"}</Label>
                  <Input value={a.subject} onChange={(e) => setAction(i, { subject: e.target.value })} className="mt-1 bg-white" data-testid={`rule-action-subject-${i}`} />
                </div>
                <div className="col-span-12">
                  <Label className="text-xs">Mensagem</Label>
                  <Textarea rows={2} value={a.message} onChange={(e) => setAction(i, { message: e.target.value })} placeholder="Clique nos campos abaixo para inserir dados do evento" className="mt-1 bg-white" data-testid={`rule-action-message-${i}`} />
                  {tokens.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {tokens.map((t) => (
                        <button key={t} type="button" onClick={() => insertToken(i, t)} title={`Insere {${t}} na mensagem`} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100" data-testid={`rule-token-${i}-${t}`}>
                          + {tokenLabels[t] || t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
